<main id="main-content" class="main-content">
  @if (errorList.length > 0) {
    <div class="red-warning">
      <mat-icon class="red-warn-icon">warning</mat-icon>
      <div class="red-warn-text">
        <span class="red-title">Incomplete section</span>
        <span class="red-text">
          The following fields are missing details. Please complete them before proceeding to the next section:
          <ul>
            @for (error of errorList; track error) {
              <li>{{error | titlecase}}</li>
            }
          </ul>
        </span>
      </div>
    </div>
  }
  <div class="subtitle"><span class="red-normal">*</span>Indicates required field</div>
  <h2 class="form-header">3. Watershed Condition</h2>

  <mat-card [formGroup]="form" style="padding-bottom: 24px;">
    <div class="text-div">
      <h2 style="font-weight: 700; font-size: 17px; margin-bottom: 5px;" id="soil-burn-severity-heading">Soil Burn Severity</h2>
      <b>@if (!optional()) {
        <span class="red">*</span>
      }Burn Severity (acres)</b>@if (optional()) {
      <span class="optional">(Optional)</span>
    }
    <br>
      <span class="gray-text">Burn severity acres by ownership</span>
    </div>
    <ng-container *ngTemplateOutlet="gisWarn"></ng-container>
    <!-- TABLE Ownership -->
    <div class="table-container">
      <table aria-labelledby="soil-burn-severity-heading soil-burn-severity-caption">
        <caption id="soil-burn-severity-caption" class="usa-sr-only">
          Soil Burn Severity
        </caption>
        <tr class="table-header">
          <th scope="col">Category</th>
          <th scope="col" class="right">Developed</th>
          <th scope="col" class="right">Unburned/<wbr>Very Low</th>
          <th scope="col" class="right">Low</th>
          <th scope="col" class="right">Moderate</th>
          <th scope="col" class="right">High</th>
          <th scope="col" class="right" style="min-width: 110px;">Total Acres</th>
        </tr>
        <ng-container formArrayName="acresBurned">
          @for (data of acresBurned.controls; track data; let i = $index) {
            <tr [formGroupName]="i" class="table-row">
              @if (data.value.ownership == "Other Federal") {
                <td style="width: 70%" class="text-cell" style="display: flex; height: 70px">
                  <div class="vert-center">{{ data.value.ownership }}</div>
                  <div class="vert-center">
                    <mat-icon
                      matTooltip='Other Federal: ARMY, BIA, BLM, BOP, BPA, DOD, DOE, DOI, DOT, FHA, FWS, GSA, VA, USPS, USMC, USDA, USCG, USAF, USACE, USBR, OTHFE, HHS, IBWC, LG, NAVY, NOAA, NPS, NTVALL, NTVPIC'
                      matTooltipClass="tooltipClass"
                    class="info-blue-header fa-regular fa-circle-info">info_outline</mat-icon>
                  </div>
                </td>
              } @else if (data.value.ownership == "Private") {
                <td style="width: 70%" class="text-cell" style="display: flex; height: 70px">
                  <div class="vert-center">{{ data.value.ownership }}</div>
                  <div class="vert-center">
                    <mat-icon matTooltip='Private or Undetermined' matTooltipClass="tooltipClass"
                    class="info-blue-header fa-regular fa-circle-info">info_outline</mat-icon>
                  </div>
                </td>
              } @else {
                <td style="width: 70%" class="text-cell">
                  {{ data.value.ownership }}
                </td>
              }
              <td>
                <mat-form-field appearance="outline" class="example small-field">
                  <input class="right" formControlName="burn_acres_developed" matInput
                    aria-label="burn_acres_developed" thousandSeparator="," mask="separator"
                    decimalMarker="." required>
                  </mat-form-field>
                </td>
                <td>
                  <mat-form-field appearance="outline" class="example small-field">
                    <input class="right" formControlName="burn_acres_unburned" matInput
                      aria-label="burn_acres_unburned" thousandSeparator="," mask="separator"
                      decimalMarker="." required>
                    </mat-form-field>
                  </td>
                  <td>
                    <mat-form-field appearance="outline" class="example small-field">
                      <input class="right" formControlName="burn_acres_low" matInput
                        aria-label="burn_acres_low" thousandSeparator="," mask="separator"
                        decimalMarker="." required>
                      </mat-form-field>
                    </td>
                    <td>
                      <mat-form-field appearance="outline" class="example small-field">
                        <input class="right" formControlName="burn_acres_moderate" matInput
                          aria-label="burn_acres_moderate" thousandSeparator="," mask="separator"
                          decimalMarker="." required>
                        </mat-form-field>
                      </td>
                      <td>
                        <mat-form-field appearance="outline" class="example small-field">
                          <input class="right" formControlName="burn_acres_high" matInput
                            aria-label="burn_acres_high" thousandSeparator="," mask="separator"
                            decimalMarker="." required>
                          </mat-form-field>
                        </td>
                        <td class="right sum-column">
                          {{categoryTotal(data.value.ownership) | number: '1.0-0' }}
                        </td>
                      </tr>
                    }
                    <tr class="sum-row">
                      <td class="text-cell">
                        Total Acres
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_developed', false) | number: '1.0-0' }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_unburned', false) | number: '1.0-0' }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_low', false) | number: '1.0-0' }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_moderate', false) | number: '1.0-0' }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_high', false) | number: '1.0-0' }}
                      </td>
                      <td class="right sum-column">
                        {{categoryTotal('total') | number: '1.0-0' }}
                      </td>
                    </tr>
                    <tr class="sum-row">
                      <td class="text-cell">
                        SBS % of NFS
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_developed', true) / (sumAcresBurned('burn_acres_developed',
                        false) || 1) | percent }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_unburned', true) / (sumAcresBurned('burn_acres_unburned',
                        false) || 1) | percent }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_low', true) / (sumAcresBurned('burn_acres_low', false) || 1) |
                        percent }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_moderate', true) / (sumAcresBurned('burn_acres_moderate',
                        false) || 1) | percent }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_high', true) / (sumAcresBurned('burn_acres_high', false) || 1)
                        | percent }}
                      </td>
                      <td class="right sum-column">
                        {{categoryTotal('nfs') / categoryTotal('total') | percent }}
                      </td>
                    </tr>
                    <tr class="sum-row">
                      <td class="text-cell">
                        SBS % of Fire
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_developed', false) / (categoryTotal('total') || 1) | percent
                        }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_unburned', false) / ( categoryTotal('total') || 1) | percent
                        }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_low', false) / ( categoryTotal('total') || 1) | percent }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_moderate', false) / ( categoryTotal('total') || 1) | percent
                        }}
                      </td>
                      <td class="right">
                        {{ sumAcresBurned('burn_acres_high', false) / ( categoryTotal('total') || 1) | percent }}
                      </td>
                      <td class="right">
                        {{ 1 | percent }}
                      </td>
                    </tr>
                  </ng-container>
                </table>
              </div>
              <div class="text-div">
                <b>@if (!optional()) {
                  <span class="red">*</span>
                }Soil Burn Severity</b>@if (optional()) {
                <span class="optional">(Optional)</span>
              }
              <br>
                <span class="gray-text">Please describe the soil burn severity.</span>
              </div>
              <!-- Rich Text input -->
              <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
              <div class="NgxEditor__Wrapper">
                <ngx-editor-menu [editor]="editor0" [toolbar]="toolbar"></ngx-editor-menu>
                <ngx-editor formControlName="burn_severity_description" [editor]="editor0" placeholder="Enter Description"
                required (ngModelChange)="onChangeEditor($event, 'burn_severity_description')"></ngx-editor>
              </div>
              <span class="gray-text text-count"
                [ngClass]="{ 'text-count-red': (form.controls.burn_severity_description.invalid && form.controls.burn_severity_description.touched) }">
                {{form.controls.burn_severity_description.value?.length ?? 0 | number:'1.0':'en-US'}} / 6,000 characters
                used
              </span>
              @if (form.controls.burn_severity_description.invalid && form.controls.burn_severity_description.touched && (form.controls.burn_severity_description.value?.length ?? 0) > 0) {
                <div
                  class="editor-error">
                  <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                  remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                </div>
              }
              <!-- Image upload -->
              <image-upload text="Please upload the burn Soil Burn Severity map (optional)" [report_id]="report().report_id"
                [supporting_files]="report().supporting_files" [saveSubject]="saveSubject" section="3"
              question="burn_severity"></image-upload>

              <div class="text-div">
                <b>@if (!optional()) {
                  <span class="red">*</span>
                }Soil Erosion Hazard and Anticipated Response</b>@if (optional()) {
                <span class="optional">(Optional)</span>
              }
              <br>
                <span class="gray-text">Please describe the erosion hazard and/or potential of the burned area, including
                the anticipated change of erosion and sedimentation response due to the fire.</span>
              </div>
              <!-- Rich Text input -->
              <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
              <div class="NgxEditor__Wrapper">
                <ngx-editor-menu [editor]="editor1" [toolbar]="toolbar"></ngx-editor-menu>
                <ngx-editor formControlName="erosion_potential" [editor]="editor1" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'erosion_potential')"></ngx-editor>
              </div>
              <span class="gray-text text-count"
                [ngClass]="{ 'text-count-red': (form.controls.erosion_potential.invalid && form.controls.erosion_potential.touched) }">
                {{form.controls.erosion_potential.value?.length ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used
              </span>
              @if (form.controls.erosion_potential.invalid && form.controls.erosion_potential.touched && (form.controls.erosion_potential.value?.length ?? 0) > 0) {
                <div
                  class="editor-error">
                  <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                  remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                </div>
              }
              <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
              [saveSubject]="saveSubject" section="3" question="soil_erosion"></image-upload>

              <div class="text-div">
                <b>@if (!optional()) {
                  <span class="red">*</span>
                }Debris Flow Potential</b>@if (optional()) {
                <span class="optional">(Optional)</span>
              }
              <br>
                <span class="gray-text">Please describe the debris flow hazard and potential of the burned area, including
                any interpretation of the USGS Post-fire Debris Flow Hazards model.</span>
              </div>
              <!-- Rich Text input -->
              <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
              <div class="NgxEditor__Wrapper">
                <ngx-editor-menu [editor]="editor2" [toolbar]="toolbar"></ngx-editor-menu>
                <ngx-editor formControlName="debris_potential" [editor]="editor2" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'debris_potential')"></ngx-editor>
              </div>
              <span class="gray-text text-count"
                [ngClass]="{ 'text-count-red': (form.controls.debris_potential.invalid && form.controls.debris_potential.touched) }">
                {{form.controls.debris_potential.value?.length ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used
              </span>
              @if (form.controls.debris_potential.invalid && form.controls.debris_potential.touched && (form.controls.debris_potential.value?.length ?? 0) > 0) {
                <div
                  class="editor-error">
                  <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                  remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                </div>
              }
              <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
              [saveSubject]="saveSubject" section="3" question="debris_flow"></image-upload>

              <div class="text-div">
                <b>@if (!optional()) {
                  <span class="red">*</span>
                }Estimated Vegetative Recovery Period (years)</b>@if (optional()) {
                <span class="optional">(Optional)</span>
              }
              <br>
                <span class="gray-text">Please describe the period of time, in years, that is anticipated for the burned
                  area to recover vegetation to recover sufficient to reduce runoff and erosion potential (approximately
                  60% ground cover). This is a best estimate of natural
                regeneration.</span>
              </div>
              <!-- Rich Text input -->
              <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
              <div class="NgxEditor__Wrapper">
                <ngx-editor-menu [editor]="editor3" [toolbar]="toolbar"></ngx-editor-menu>
                <ngx-editor formControlName="vegetative_recovery" [editor]="editor3" placeholder="Enter Description"
                required (ngModelChange)="onChangeEditor($event, 'vegetative_recovery')"></ngx-editor>
              </div>
              <span class="gray-text text-count"
                [ngClass]="{ 'text-count-red': (form.controls.vegetative_recovery.invalid && form.controls.vegetative_recovery.touched) }">
                {{form.controls.vegetative_recovery.value?.length ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used
              </span>
              @if (form.controls.vegetative_recovery.invalid && form.controls.vegetative_recovery.touched && (form.controls.vegetative_recovery.value?.length ?? 0) > 0) {
                <div
                  class="editor-error">
                  <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                  remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                </div>
              }
              <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
              [saveSubject]="saveSubject" section="3" question="vegetation_rec"></image-upload>

              <div class="text-div">
                <b>@if (!optional()) {
                  <span class="red">*</span>
                }Estimated Hydrologic Response</b>@if (optional()) {
                <span class="optional">(Optional)</span>
              }
              <br>
                <span class="gray-text">Provide a brief narrative that describes the predicted hydrologic response due to
                  post-fire conditions within the burned
                area. Also describe the predicted storm type and time of year of the first damaging storm.</span>
              </div>
              <!-- Rich Text input -->
              <span class="gray-text tip-text"><b>Tip:</b> Use Ctrl+Shift+V to paste as plain text.</span>
              <div class="NgxEditor__Wrapper">
                <ngx-editor-menu [editor]="editor4" [toolbar]="toolbar"></ngx-editor-menu>
                <ngx-editor formControlName="hydrolic_response" [editor]="editor4" placeholder="Enter Description" required
                (ngModelChange)="onChangeEditor($event, 'hydrolic_response')"></ngx-editor>
              </div>
              <span class="gray-text text-count"
                [ngClass]="{ 'text-count-red': (form.controls.hydrolic_response.invalid && form.controls.hydrolic_response.touched) }">
                {{form.controls.hydrolic_response.value?.length ?? 0 | number:'1.0':'en-US'}} / 6,000 characters used
              </span>
              @if (form.controls.hydrolic_response.invalid && form.controls.hydrolic_response.touched && (form.controls.hydrolic_response.value?.length ?? 0) > 0) {
                <div
                  class="editor-error">
                  <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon> Please shorten your text or
                  remove hidden formatting (e.g., paste as plain text using Ctrl+Shift+V).
                </div>
              }
              <image-upload [report_id]="report().report_id" [supporting_files]="report().supporting_files"
              [saveSubject]="saveSubject" section="3" question="hydrologic_res"></image-upload>


            </mat-card>
          </main>

          <ng-template #gisWarn>
            <div class="yellow-warning">
              <fa-icon [icon]="faWarning" class="faWarning" aria-label="Warning"></fa-icon>
              <div class="support-text">
                <b>Data source:</b> GIS data. Manual edits made below will be overwritten when GIS data is updated.
              </div>
            </div>
          </ng-template>
